import type { ApiClient } from '../client';
import type {
  TranslationRequest,
  TranslationResponse,
  VoiceSessionStartRequest,
  VoiceSessionStartResponse,
  VoiceSegment,
  OcrRequest,
  OcrResponse,
  HistoryItem,
  HistoryQuery,
  VocabularyItem,
  VocabularyQuery,
  UserProfile,
  Entitlement,
  PagedResponse,
} from '../types';

export interface TranslationApi {
  translate(request: TranslationRequest): Promise<TranslationResponse>;
}

export function createTranslationApi(client: ApiClient): TranslationApi {
  return {
    translate: request => client.post<TranslationResponse>('/api/translate', request),
  };
}

export interface VoiceApi {
  startSession(request: VoiceSessionStartRequest): Promise<VoiceSessionStartResponse>;
  sendSegment(sessionId: string, audioBase64: string, mimeType: string): Promise<VoiceSegment>;
}

export function createVoiceApi(client: ApiClient): VoiceApi {
  return {
    startSession: request => client.post<VoiceSessionStartResponse>('/api/voice/sessions', request),
    sendSegment: (sessionId, audioBase64, mimeType) =>
      client.post<VoiceSegment>(`/api/voice/sessions/${encodeURIComponent(sessionId)}/segments`, {
        audioBase64,
        mimeType,
      }),
  };
}

export interface OcrApi {
  process(request: OcrRequest): Promise<OcrResponse>;
}

export function createOcrApi(client: ApiClient): OcrApi {
  return {
    process: request => {
      const formData = new FormData();
      formData.append('file', request.imageFile as unknown as Blob);
      formData.append('targetLanguage', request.targetLanguage);
      if (request.register) {
        formData.append('register', request.register);
      }
      return client.post<OcrResponse>('/api/ocr', formData);
    },
  };
}

/**
 * History list + per-item deletion. There is no bulk `DELETE /api/history`
 * route: "clear all history" is realized by account deletion — call
 * `AccountApi.deleteAccount()` (`DELETE /api/user/account`), whose FK-lineage
 * cascade removes translations and their derived vocabulary/decks (FR-5.4).
 * This matches the shipped web parity (REQ-UI-005), which maps its "delete all"
 * affordance to account deletion.
 */
export interface HistoryApi {
  getHistory(query?: HistoryQuery): Promise<PagedResponse<HistoryItem>>;
  deleteHistoryItem(id: string): Promise<void>;
}

export function createHistoryApi(client: ApiClient): HistoryApi {
  return {
    getHistory: (query = {}) => {
      const params = new URLSearchParams();
      params.set('page', String(query.page ?? 1));
      params.set('limit', String(query.limit ?? 20));
      if (query.type) params.set('type', query.type);
      return client.get<PagedResponse<HistoryItem>>(`/api/history?${params}`);
    },
    deleteHistoryItem: id => client.delete<void>(`/api/history/${encodeURIComponent(id)}`),
  };
}

export interface VocabularyApi {
  /**
   * Page-based list. Path is `/api/learning/vocabulary` (not `/api/vocabulary`)
   * to match the server mount in `apps/api` vocabulary-route.
   */
  getVocabulary(query?: VocabularyQuery): Promise<PagedResponse<VocabularyItem>>;
}

export function createVocabularyApi(client: ApiClient): VocabularyApi {
  return {
    getVocabulary: (query = {}) => {
      const params = new URLSearchParams();
      params.set('page', String(query.page ?? 1));
      params.set('limit', String(query.limit ?? 50));
      return client.get<PagedResponse<VocabularyItem>>(`/api/learning/vocabulary?${params}`);
    },
  };
}

export interface AccountApi {
  getProfile(): Promise<UserProfile>;
  deleteAccount(): Promise<void>;
}

export function createAccountApi(client: ApiClient): AccountApi {
  return {
    getProfile: () => client.get<UserProfile>('/api/user/profile'),
    deleteAccount: () => client.delete<void>('/api/user/account'),
  };
}

export interface BillingApi {
  getEntitlement(): Promise<Entitlement>;
}

export function createBillingApi(client: ApiClient): BillingApi {
  return {
    getEntitlement: () => client.get<Entitlement>('/api/billing/entitlement'),
  };
}

export { createLearningApi } from './learning';
